import React from "react";
import { useAuth } from "../../../../../../../../../firebase/AuthContext";

export default function PinningModeMenu({ language, onSelectMode }) {
    // Pinning (manual and automatic) needs api.pins.manage; allowed origins needs api.edit.
    const { hasRole } = useAuth();
    const canPin = hasRole("api.pins.manage");
    const canEdit = hasRole("api.edit");
    return (
        <div className="flex-1 flex flex-col items-center justify-center py-10 px-8">
            <p className="text-sm text-gray-500 mb-6">
                {language === "zh"
                    ? "選擇此頻道的版本綁定策略"
                    : "Choose a version pinning strategy for this channel"}
            </p>

            {canPin && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-lg">
                {/* Manual Pinning Card */}
                <button
                    onClick={() => onSelectMode("manual")}
                    className="group relative bg-white border-2 border-gray-200 hover:border-orange-400 rounded-2xl p-6 text-left transition-all duration-200 hover:shadow-lg hover:shadow-orange-500/10"
                >
                    <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg
                            className="w-5 h-5 text-orange-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                            />
                        </svg>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center mb-4">
                        <svg
                            className="w-6 h-6 text-orange-500"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
                        </svg>
                    </div>
                    <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-orange-500 transition-colors">
                        {language === "zh" ? "手動綁定" : "Manual Pinning"}
                    </h3>
                    <p className="text-xs text-gray-400 leading-relaxed">
                        {language === "zh"
                            ? "手動選擇每個產品要顯示的特定版本。完全掌控每個語言的版本。"
                            : "Manually select which specific version to show for each product. Full control over versions per language."}
                    </p>
                </button>

                {/* Automatic Pinning Card */}
                <button
                    onClick={() => onSelectMode("automatic")}
                    className="group relative bg-white border-2 border-gray-200 hover:border-blue-400 rounded-2xl p-6 text-left transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/10"
                >
                    <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg
                            className="w-5 h-5 text-blue-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                            />
                        </svg>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                        <svg
                            className="w-6 h-6 text-blue-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M13 10V3L4 14h7v7l9-11h-7z"
                            />
                        </svg>
                    </div>
                    <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-blue-500 transition-colors">
                        {language === "zh" ? "自動綁定" : "Automatic Pinning"}
                    </h3>
                    <p className="text-xs text-gray-400 leading-relaxed">
                        {language === "zh"
                            ? "自動使用最新上傳的版本。適合想要始終顯示最新資料的頻道。"
                            : "Automatically use the latest uploaded version. Ideal for channels that always want the newest data."}
                    </p>
                </button>
            </div>
            )}

            {/* Allowed URLs — full-width below the 2-col grid */}
            {canEdit && (
            <div className="w-full max-w-lg mt-4">
                <button
                    onClick={() => onSelectMode("allowed_urls")}
                    className="group relative w-full bg-white border-2 border-gray-200 hover:border-purple-400 rounded-2xl p-5 text-left transition-all duration-200 hover:shadow-lg hover:shadow-purple-500/10 flex items-center gap-5"
                >
                    <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg
                            className="w-5 h-5 text-purple-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                            />
                        </svg>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <svg
                            className="w-5 h-5 text-purple-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                            />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-base font-bold text-gray-900 mb-0.5 group-hover:text-purple-600 transition-colors">
                            {language === "zh"
                                ? "允許的來源網址 (CORS)"
                                : "Allowed Origins (CORS)"}
                        </h3>
                        <p className="text-xs text-gray-400 leading-relaxed">
                            {language === "zh"
                                ? "設定哪些來源網址可以呼叫此頻道的公開 API。留空表示允許所有來源。"
                                : "Control which origins can call this channel's public API. Leave empty to allow all."}
                        </p>
                    </div>
                </button>
            </div>
            )}
        </div>
    );
}
